import { useEffect, useMemo, useState } from 'react';
import api from '../lib/apiClient';
import { Modal } from './Modal';
import { IconEvents, IconLeave } from './icons';
import { labelFor, useConfig } from '../context/ConfigContext';

interface CalendarEvent {
  id: number;
  title: string;
  start_at: string;
  end_at: string | null;
  is_public_holiday: boolean;
}

interface CalendarLeave {
  id: number;
  user_name: string;
  is_mine: boolean;
  type: string;
  start_date: string;
  end_date: string;
  status: 'pending' | 'approved' | 'rejected';
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Every calendar date an item covers, inclusive — used to spread a
// multi-day leave request across each day of its span.
function datesBetween(startIso: string, endIso: string): string[] {
  const dates: string[] = [];
  const cursor = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  while (cursor <= end) {
    dates.push(isoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export function EventCalendar() {
  const { lists } = useConfig();
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [leaves, setLeaves] = useState<CalendarLeave[]>([]);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const monthStart = viewDate;
  const monthEnd = useMemo(() => new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0), [viewDate]);

  useEffect(() => {
    const params = { date_from: isoDate(monthStart), date_to: isoDate(monthEnd) };
    api
      .get<{ data: CalendarEvent[] }>('/events', { params: { ...params, per_page: 100 } })
      .then(({ data }) => setEvents(data.data))
      .catch(() => setEvents([]));
    api
      .get<CalendarLeave[]>('/leave-requests/mine', { params })
      .then(({ data }) => setLeaves(data))
      .catch(() => setLeaves([]));
  }, [monthStart, monthEnd]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const key = isoDate(new Date(event.start_at));
      map.set(key, [...(map.get(key) ?? []), event]);
    }
    return map;
  }, [events]);

  const leavesByDay = useMemo(() => {
    const map = new Map<string, CalendarLeave[]>();
    for (const leave of leaves) {
      for (const day of datesBetween(leave.start_date, leave.end_date)) {
        map.set(day, [...(map.get(day) ?? []), leave]);
      }
    }
    return map;
  }, [leaves]);

  const weeks = useMemo(() => {
    const firstWeekday = monthStart.getDay();
    const daysInMonth = monthEnd.getDate();
    const cells: (Date | null)[] = [
      ...Array<null>(firstWeekday).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(viewDate.getFullYear(), viewDate.getMonth(), i + 1)),
    ];
    while (cells.length % 7 !== 0) cells.push(null);

    const rows: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [monthStart, monthEnd, viewDate]);

  const today = isoDate(new Date());
  const monthLabel = viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const selectedEvents = selectedDay ? (eventsByDay.get(selectedDay) ?? []) : [];
  const selectedLeaves = selectedDay ? (leavesByDay.get(selectedDay) ?? []) : [];

  return (
    <div className="card">
      <div className="calendar-header">
        <h2>{monthLabel}</h2>
        <div className="calendar-nav">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
          >
            ‹
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setViewDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
          >
            Today
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
          >
            ›
          </button>
        </div>
      </div>

      <div className="calendar-grid">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="calendar-weekday">
            {label}
          </div>
        ))}
        {weeks.map((week, i) =>
          week.map((day, j) => {
            if (!day) return <div key={`${i}-${j}`} className="calendar-day empty" />;
            const key = isoDate(day);
            const dayEvents = eventsByDay.get(key) ?? [];
            const dayLeaves = leavesByDay.get(key) ?? [];
            const hasItems = dayEvents.length > 0 || dayLeaves.length > 0;
            return (
              <button
                key={key}
                type="button"
                className={`calendar-day${key === today ? ' today' : ''}`}
                onClick={() => hasItems && setSelectedDay(key)}
                disabled={!hasItems}
              >
                <span className="calendar-day-number">{day.getDate()}</span>
                {hasItems && (
                  <span className="calendar-day-icons">
                    {dayEvents.length > 0 && <IconEvents className="calendar-day-icon" />}
                    {dayLeaves.length > 0 && <IconLeave className="calendar-day-icon calendar-day-icon-leave" />}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      {selectedDay && (
        <Modal
          title={new Date(`${selectedDay}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          onClose={() => setSelectedDay(null)}
        >
          <div className="calendar-day-events">
            {selectedEvents.map((event) => (
              <div key={`event-${event.id}`} className="calendar-day-event-detail">
                <IconEvents className="calendar-day-icon" />
                <div>
                  <strong>{event.title}</strong>
                  {event.is_public_holiday && <span className="badge badge-active"> Public Holiday</span>}
                </div>
              </div>
            ))}
            {selectedLeaves.map((leave) => (
              <div key={`leave-${leave.id}`} className="calendar-day-event-detail">
                <IconLeave className="calendar-day-icon calendar-day-icon-leave" />
                <div>
                  <strong>
                    {leave.is_mine ? 'You' : leave.user_name} — {labelFor(lists, 'leave_type', leave.type)} leave
                  </strong>{' '}
                  <span className={`badge badge-${leave.status === 'approved' ? 'active' : leave.status === 'rejected' ? 'cancelled' : 'scheduled'}`}>
                    {leave.status}
                  </span>
                </div>
              </div>
            ))}
            {selectedEvents.length === 0 && selectedLeaves.length === 0 && <p className="muted">Nothing on this day.</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
